<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl"><i class="fas fa-search"></i> ค้นหา</label>
          <b-form-input
            v-model.trim="searchTable"
            type="search"
            size="sm"
            class="field-input-native"
            placeholder="ชื่อแพทย์ หรือสาขา"
          />
        </div>

        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-multi">
        <ClinicBranchMultiSelect
          :value="branchFilter"
          :options="branchOptions"
          @input="$emit('update:branchFilter', $event)"
        />
      </div>
    </div>

    <div id="report-elements">
      <ClinicBranchSummary :rows="filteredList" :metrics="summaryMetrics" />

      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport('ClinicDoctorsIncomeTable')">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport('ClinicDoctorsIncomeTable')">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        id="ClinicDoctorsIncomeTable"
        ref="ClinicDoctorsIncomeTable"
        class="my-0 sticky-footer report-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="fields"
        sort-by="branchName"
        :responsive="true"
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(index)="row">{{ row.index + 1 }}.</template>
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td v-for="field in fields" :key="field.key">
              {{ isSumField(field.key) ? formatNumber(sum(field.key)) : "" }}
            </td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import ClinicBranchSummary from "@/components/reports/ClinicBranchSummary";
import ClinicBranchMultiSelect from "@/components/reports/ClinicBranchMultiSelect";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { matchesSearchQuery } from "@/utils/textSearch";
import { branchNameOptions, matchesBranches } from "@/utils/clinicReportFilters";

export default {
  name: "ClinicDoctorsIncomeReport",
  components: {
    Dialog,
    Loading,
    ClinicBranchSummary,
    ClinicBranchMultiSelect,
  },
  props: {
    // Selected branch names, shared with the other clinic report tabs. Empty = all.
    branchFilter: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานรายได้แพทย์ (คลินิก)",
      reportNameEn: "Clinic-Income-Doctor",

      dateRange: [],

      tableBusy: false,
      // One row per (branch, doctor), income summed across the range + lab expense
      // merged in by the backend. DF/CF are gross paidDf/paidCf.
      incomeList: [],
      fields: [
        { key: "index", label: "" },
        { key: "branchName", label: "สาขา", sortable: true, class: "narrow-spacing" },
        { key: "doctorName", label: "แพทย์", sortable: true, class: "narrow-spacing" },
        { key: "price", label: "ค่ารักษา", class: "text-right", formatter: "formatNumber" },
        { key: "finalDiscount", label: "ส่วนลด", class: "text-right", formatter: "formatNumber" },
        { key: "amount", label: "สุทธิ", class: "text-right", formatter: "formatNumber" },
        { key: "paid", label: "ชำระ", class: "text-right", formatter: "formatNumber" },
        { key: "paidDf", label: "DF", class: "text-right", formatter: "formatNumber" },
        { key: "paidCf", label: "CF", class: "text-right", formatter: "formatNumber" },
        { key: "labTotalExpense", label: "ค่าแลป", class: "text-right", formatter: "formatNumber" },
        { key: "overdue", label: "ค้างชำระ", class: "text-right text-danger", formatter: "formatNumber" },
      ],
      sumKeys: ["price", "finalDiscount", "amount", "paid", "paidDf", "paidCf", "labTotalExpense", "overdue"],
      summaryMetrics: [
        { key: "price", label: "ค่ารักษา" },
        { key: "amount", label: "สุทธิ" },
        { key: "paid", label: "ชำระ" },
        { key: "paidDf", label: "DF" },
        { key: "paidCf", label: "CF" },
        { key: "labTotalExpense", label: "ค่าแลป" },
      ],

      searchTable: "",
      currentPage: 1,
      perPage: 100,
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    branchOptions() {
      return branchNameOptions(this.incomeList);
    },
    filteredList() {
      return this.incomeList
        .filter(i => matchesBranches(i.branchName, this.branchFilter))
        .filter(i => matchesSearchQuery(i, this.searchTable, ["doctorName", "branchName"]))
        .slice(0)
        .sort((a, b) => {
          const byBranch = String(a.branchName || "").localeCompare(
            String(b.branchName || ""),
            "th"
          );
          if (byBranch !== 0) return byBranch;
          return String(a.doctorName || "").localeCompare(String(b.doctorName || ""), "th");
        });
    },
    startDt() {
      return this.dateRange[0] ? moment(this.dateRange[0]).startOf("day").format() : "";
    },
    endDt() {
      return this.dateRange[1] ? moment(this.dateRange[1]).endOf("day").format() : "";
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
  },
  watch: {
    searchTable() {
      this.currentPage = 1;
    },
    branchFilter() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchClinicDoctorIncome: "moduleReport/fetchClinicDoctorIncome",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    moment() {
      return moment();
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isSumField(key) {
      return this.sumKeys.includes(key);
    },
    sum(key) {
      if (!this.isSumField(key)) return null;
      return this.filteredList.reduce((accum, item) => {
        const data = Number(item[key]);
        if (isNaN(data)) return accum;
        return Math.round((accum + data) * 100) / 100;
      }, 0);
    },
    fetchReport() {
      if (this.startDt === "" || this.endDt === "") return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.fetchClinicDoctorIncome({
        clinicUrl: this.$route.params.clinicUrl,
        startDt: this.startDt,
        endDt: this.endDt,
      })
        .then(res => {
          this.incomeList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async excelExport(id) {
      this.perPage = null;
      await this.$nextTick();
      exportTableToExcel({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
      this.perPage = 100;
    },
    async pdfExport(id) {
      this.perPage = null;
      await this.$nextTick();
      exportReportToPdf({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
      this.perPage = 100;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style scoped></style>
